import { LOCALES, type Category, type CategoryKind } from '@entities/taxonomy';

export const KIND_LABEL: Record<CategoryKind, string> = {
  PRODUCER: 'Производители',
  ECO_PRODUCER: 'Эко-производители',
  PRODUCT: 'Товары',
};

export const KIND_HINT: Record<CategoryKind, string> = {
  PRODUCER: 'Отрасли, в которых работает производитель',
  ECO_PRODUCER: 'Направления эко-производства',
  PRODUCT: 'Товарные категории маркетплейса и RFQ',
};

export interface TreeNode {
  category: Category;
  children: TreeNode[];
}

/** Groups a flat list into a sorted forest, treating orphans as roots. */
export function buildForest(all: Category[]): TreeNode[] {
  const ids = new Set(all.map((c) => c.id));
  const nodes = new Map<string, TreeNode>(all.map((c) => [c.id, { category: c, children: [] }]));
  const roots: TreeNode[] = [];

  for (const c of all) {
    const node = nodes.get(c.id)!;
    if (c.parentId && ids.has(c.parentId)) nodes.get(c.parentId)!.children.push(node);
    else roots.push(node);
  }

  const sort = (list: TreeNode[]) => {
    list.sort(
      (a, b) =>
        a.category.sortOrder - b.category.sortOrder ||
        a.category.slug.localeCompare(b.category.slug),
    );
    list.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

/** Matches against the slug and every locale, so search works regardless of UI language. */
function matches(c: Category, needle: string): boolean {
  if (c.slug.toLowerCase().includes(needle)) return true;
  return LOCALES.some((l) => (c.name?.[l] ?? '').toLowerCase().includes(needle));
}

/** Prunes the forest to matching nodes, keeping ancestors so hierarchy stays readable. */
export function filterForest(forest: TreeNode[], query: string): TreeNode[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return forest;

  const prune = (node: TreeNode): TreeNode | null => {
    const kids = node.children.map(prune).filter((n): n is TreeNode => n !== null);
    if (kids.length > 0 || matches(node.category, needle)) {
      return { category: node.category, children: kids };
    }
    return null;
  };
  return forest.map(prune).filter((n): n is TreeNode => n !== null);
}

export function collectIds(forest: TreeNode[]): string[] {
  const out: string[] = [];
  const walk = (list: TreeNode[]) => {
    for (const n of list) {
      out.push(n.category.id);
      walk(n.children);
    }
  };
  walk(forest);
  return out;
}

/** Flattens a kind's categories into indented labels for the parent picker. */
export function parentOptions(all: Category[]): { id: string; label: string }[] {
  const ids = new Set(all.map((c) => c.id));
  const children = new Map<string | null, Category[]>();
  for (const c of all) {
    const key = c.parentId && ids.has(c.parentId) ? c.parentId : null;
    children.set(key, [...(children.get(key) ?? []), c]);
  }
  for (const bucket of children.values()) {
    bucket.sort((a, b) => a.sortOrder - b.sortOrder || a.slug.localeCompare(b.slug));
  }
  const out: { id: string; label: string }[] = [];
  const walk = (list: Category[], depth: number) => {
    for (const c of list) {
      out.push({ id: c.id, label: `${'  '.repeat(depth)}${depth > 0 ? '└ ' : ''}${c.slug}` });
      walk(children.get(c.id) ?? [], depth + 1);
    }
  };
  walk(children.get(null) ?? [], 0);
  return out;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 128);
}
